import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, User, Bell, Shield, Globe, Link2 } from 'lucide-react';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('workspace');

  const tabs = [
    { id: 'workspace', label: 'Workspace', icon: <Settings size={20} /> },
    { id: 'profile', label: 'Profil', icon: <User size={20} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={20} /> },
    { id: 'security', label: 'Sécurité', icon: <Shield size={20} /> },
    { id: 'connections', label: 'Connexions OAuth', icon: <Link2 size={20} /> },
    { id: 'language', label: 'Langue', icon: <Globe size={20} /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Settings className="inline mr-3 text-cyan-400" size={36} />
          Paramètres
        </h1>
        <p className="text-slate-400 text-lg">Gérez votre workspace et vos préférences</p>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-3">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <motion.button
                    key={tab.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeTab === tab.id
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {tab.icon}
                    <span className="font-medium">{tab.label}</span>
                  </motion.button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-12 lg:col-span-9">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
              {activeTab === 'workspace' && <WorkspaceSettings />}
              {activeTab === 'profile' && <ProfileSettings />}
              {activeTab === 'notifications' && <NotificationsSettings />}
              {activeTab === 'security' && <SecuritySettings />}
              {activeTab === 'connections' && <OAuthConnections />}
              {activeTab === 'language' && <LanguageSettings />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Workspace Settings Component
const WorkspaceSettings = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Workspace</h2>
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Nom du Workspace</label>
        <input
          type="text"
          defaultValue="NEXORA AI"
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
        <textarea
          defaultValue="Plateforme de publication intelligente"
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500 h-24"
        />
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold"
      >
        Enregistrer les modifications
      </motion.button>
    </div>
  </div>
);

// Profile Settings Component
const ProfileSettings = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Profil Utilisateur</h2>
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Prénom</label>
          <input
            type="text"
            className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Nom</label>
          <input
            type="text"
            className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
        <input
          type="email"
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
        />
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold"
      >
        Mettre à jour le profil
      </motion.button>
    </div>
  </div>
);

// Notifications Settings Component
const NotificationsSettings = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Notifications</h2>
    <div className="space-y-4">
      <label className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-white/10">
        <span className="text-white">Notifications de publication</span>
        <input type="checkbox" defaultChecked className="w-5 h-5 rounded" />
      </label>
      <label className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-white/10">
        <span className="text-white">Alertes d&apos;objectifs</span>
        <input type="checkbox" defaultChecked className="w-5 h-5 rounded" />
      </label>
      <label className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-white/10">
        <span className="text-white">Insights quotidiens</span>
        <input type="checkbox" className="w-5 h-5 rounded" />
      </label>
    </div>
  </div>
);

// Security Settings Component
const SecuritySettings = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Sécurité</h2>
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Mot de passe actuel</label>
        <input
          type="password"
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Nouveau mot de passe</label>
        <input
          type="password"
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Confirmer le mot de passe</label>
        <input
          type="password"
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
        />
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold"
      >
        Changer le mot de passe
      </motion.button>
    </div>
  </div>
);

// OAuth Connections Component
const OAuthConnections = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Connexions OAuth</h2>
    <p className="text-slate-400 mb-6">Gérez vos connexions aux réseaux sociaux depuis la page Connecteurs</p>
    <motion.a
      href="/connectors"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="inline-block px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold"
    >
      Gérer les connecteurs
    </motion.a>
  </div>
);

// Language Settings Component
const LanguageSettings = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Langue et Région</h2>
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Langue de l&apos;interface</label>
        <select className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500">
          <option value="fr">Français</option>
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Fuseau horaire</label>
        <select className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500">
          <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
          <option value="America/New_York">America/New_York (GMT-5)</option>
          <option value="Asia/Tokyo">Asia/Tokyo (GMT+9)</option>
        </select>
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold"
      >
        Enregistrer les préférences
      </motion.button>
    </div>
  </div>
);

export default SettingsPage;
